<template>
  <div class="u-containar-md" style="overflow: hidden">
    <HeadingLv2 text="病院見学の申込み" simple />
    <FlowVisitApplication :current="state.currentFlow" />
  </div>

  <router-view
    v-if="state.enableRequest"
    :state="state"
    :data="data"
    :err="errors"
    :notEnterdData="notEnterdData"
    @update-data="updateData"
    @update-flow="updateFlow"
    @update-landing-state="updateLandingState"
    @update-error-default-input-data="updateErrorDefaultInputData"
    @catch-error="catchError"
  ></router-view>
  <div class="u-containar-md" v-if="!state.enableRequest">
    <Alert
      text="必須項目が未入力のため、お申込みいただけません。恐れ入りますがマイページ内から会員情報を更新し、再度お申込みください。"
    />
  </div>
</template>

<script lang="ts">
import { reactive, ref } from 'vue'
import store from '@/store/'
import HeadingLv2 from '@/components/elements/HeadingLv2.vue'
import FlowVisitApplication from '@/components/block/FlowVisitApplication.vue'
import Alert from '@/components/elements/Alert.vue'
import { axiosSetting } from '@/utils/axiosSetting'
import {
  getBirthdayPreviewHtml,
  getAddressPreviewHtml
} from '@/utils/commonMethods'

export default {
  name: 'VisitApplication',

  components: {
    HeadingLv2,
    FlowVisitApplication,
    Alert
  },

  props: {
    hospitalData: {
      type: Object,
      default: () => ({})
    },
    userData: {
      type: Object,
      default: () => ({})
    }
  },

  beforeRouteEnter: async (to: any, from: any, next: any) => {
    // 遷移元のページをCookieに保存
    if (from.href) {
      document.cookie = `visitApplicationFrom=${from.href}; max-age=3600; path=/`
    }

    if (!store.state.isLogin) {
      return next()
    }

    const promise = []
    let hospitalData: any = {}
    let userData: any = {}

    // 病院情報取得
    const ajax = axiosSetting('app', 'block')
    // ユーザー情報取得
    promise[0] = ajax.get('/api/v1/userInfo?is_all=1').then((res) => {
      return res.data
    })

    promise[1] = ajax
      .get(`/api/v1/detail?hospital_id=${to.params.id}`)
      .then((res) => {
        return res.data
      })

    await Promise.all(promise).then((data: any) => {
      for (let i = 0; i < data.length; i++) {
        if (i === 0) {
          userData = data[i]
        } else {
          // 病院名がない場合か有料病院ではない場合は404にする
          if (!data[i].hospital_name || !data[i].paid_flg) {
            next('404')
            break
          }
          hospitalData = data[i]
        }
      }
    })

    // 病院データを親コンポーネントに連携
    // ユーザーデータを親コンポーネントに連携
    to.matched[0].props.default = {
      hospitalData: hospitalData,
      userData: userData
    }

    next()
  },

  setup(props: any) {
    const state = reactive({
      isLandingInput: false,
      currentFlow: 1,
      enableRequest: true,
      isErrorDefaultInputData: false
    })

    const updateFlow = (value: number) => {
      state.currentFlow = value
    }

    const updateLandingState = (value: boolean) => {
      state.isLandingInput = value
    }

    const updateErrorDefaultInputData = (value: boolean) => {
      state.isErrorDefaultInputData = value
    }

    if (!store.state.isLogin) {
      return {
        state,
        data: {},
        notEnterdData: {},
        updateData: {},
        updateFlow,
        updateLandingState,
        updateErrorDefaultInputData
      }
    }

    const memberType = props.userData.member_type === '医学生' ? '1' : '2'

    if (
      memberType === '2' &&
      !props.userData.work_place &&
      !props.userData.licensing_year
    ) {
      state.enableRequest = false
    }

    if (memberType === '1' && !props.userData.graduate_year) {
      state.enableRequest = false
    }

    const data = reactive<any>({
      hospital: {
        name: {
          label: '病院名',
          content: props.hospitalData.hospital_name
        },
        address: {
          label: '住所',
          content:
            props.hospitalData.prefectures + props.hospitalData.hospital_address
        },
        tel: {
          label: '電話番号',
          content: props.hospitalData.tel
        }
      },
      contents: {
        preferred_date: {
          label: '希望見学日時',
          content: ''
        },
        preferred_note: {
          label: '希望内容、質問など',
          content: ''
        }
      },
      user: {
        name: {
          label: '姓名（漢字）',
          content: props.userData.name_sei + props.userData.name_mei
        },
        name_kana: {
          label: '姓名（カナ）',
          content: props.userData.kana_sei + props.userData.kana_mei
        },
        gender: {
          label: '性別',
          content: props.userData.gender
        },
        birthday: {
          label: '生年月日',
          content: getBirthdayPreviewHtml(props.userData.birthday)
        },
        email: {
          label: 'メールアドレス',
          content: props.userData.email
        }
      }
    })

    if (
      props.userData.zip &&
      props.userData.prefecture &&
      props.userData.city &&
      props.userData.town
    ) {
      data.user.address = {
        label: '住所',
        content: getAddressPreviewHtml(
          props.userData.zip,
          props.userData.prefecture,
          props.userData.city,
          props.userData.town
        )
      }
    }

    data.user.tel = { label: '電話番号' }
    data.user.tel.content = props.userData.tel ? props.userData.tel : ''

    if (props.userData.vacation_flg) {
      data.user.address_vacation = {
        label: '休暇中住所',
        content: '上記の住所と同じ'
      }
    } else if (
      props.userData.zip_vacation &&
      props.userData.prefecture_vacation &&
      props.userData.city_vacation &&
      props.userData.town_vacation
    ) {
      data.user.address_vacation = {
        label: '休暇中住所',
        content: getAddressPreviewHtml(
          props.userData.zip_vacation,
          props.userData.prefecture_vacation,
          props.userData.city_vacation,
          props.userData.town_vacation
        )
      }
    }

    data.user.school = {
      label: '大学名',
      content: props.userData.school_id
    }

    if (memberType === '1') {
      data.user.graduate_year = {
        label: '卒業時期（年）',
        content: props.userData.graduate_year
      }
    }
    if (memberType === '2') {
      data.user.work_place = {
        label: '現勤務先',
        content: props.userData.work_place
      }

      data.user.licensing_year = {
        label: '医師免許取得年',
        content: props.userData.licensing_year
      }
    }

    const notEnterdData = reactive<any>({
      zip: props.userData.zip || '',
      prefecture: props.userData.prefecture || '',
      city: props.userData.city || '',
      town: props.userData.town || '',
      tel: props.userData.tel || '',
      zip_vacation: props.userData.zip_vacation || '',
      prefecture_vacation: props.userData.prefecture_vacation || '',
      city_vacation: props.userData.city_vacation || '',
      town_vacation: props.userData.town_vacation || '',
      vacation_flg: props.userData.vacation_flg
    })

    const updateData = (name: string, value: string) => {
      const type =
        name === 'preferred_date' || name === 'preferred_note'
          ? 'contents'
          : 'user'

      if (type === 'contents') {
        data[type][name].content = value
      }

      if (type === 'user') {
        notEnterdData[name] = value
      }
    }

    const errors = ref<any>({})
    const catchError = (errMsg: any) => {
      errors.value = errMsg
    }

    return {
      state,
      data,
      notEnterdData,
      updateData,
      updateFlow,
      updateLandingState,
      updateErrorDefaultInputData,
      errors,
      catchError
    }
  }
}
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;
</style>
